import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import api from '../api/api';
import PacienteAlergias from './PacienteAlergias';
import { calcularEdadDetallada } from '../utils/dateUtils';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Función para convertir la imagen del QR a los bytes de la P1 (384px)
//  Enviamos la imagen centrada con margen lateral.
const prepareP1Buffer = async (imageUrl) => {
  const img = new Image();
  img.crossOrigin = "Anonymous";
  img.src = imageUrl;
  await img.decode();
 
  // Ancho nativo del cabezal de la P1 en píxeles
  const PRINT_WIDTH_PX  = 384;
  const PRINT_WIDTH_BYTES = PRINT_WIDTH_PX / 8; // = 48
 
  // Tamaño del QR que queremos imprimir (centrado)
  const QR_SIZE = 280; // px — ajusta a tu gusto (máx 384)
  const MARGIN  = Math.floor((PRINT_WIDTH_PX - QR_SIZE) / 2);
 
  // Creamos canvas con el ancho completo del cabezal
  const canvas = document.createElement('canvas');
  canvas.width  = PRINT_WIDTH_PX;
  canvas.height = QR_SIZE + 20; // imagen + pequeño margen inferior
  const ctx = canvas.getContext('2d');
 
  // Fondo blanco (importante — sin fondo = negro en la impresora)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
 
  // Dibujamos el QR centrado
  ctx.drawImage(img, MARGIN, 0, QR_SIZE, QR_SIZE);
 
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  const HEIGHT  = canvas.height;
 
  // ── Cabecera ESC/POS ──
  // ESC @ = inicializar
  // GS v 0 = modo raster (bit image)
  //   xL xH = bytes por fila (48 = 0x30, 0x00)
  //   yL yH = número de filas
  const yL = HEIGHT & 0xFF;
  const yH = (HEIGHT >> 8) & 0xFF;
 
  const header = [
    0x1B, 0x40,                           // ESC @ — Init
    0x1B, 0x33, 0x00,                     // ESC 3 n — line spacing = 0
    0x1D, 0x76, 0x30, 0x00,              // GS v 0 m=normal
    PRINT_WIDTH_BYTES, 0x00,             // xL xH
    yL, yH,                               // yL yH
  ];
 
  const body = [];
  for (let y = 0; y < HEIGHT; y++) {
    for (let xb = 0; xb < PRINT_WIDTH_BYTES; xb++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const px  = xb * 8 + bit;
        const idx = (y * PRINT_WIDTH_PX + px) * 4;
        const r   = pixels[idx];
        const g   = pixels[idx + 1];
        const b   = pixels[idx + 2];
        const lum = 0.299 * r + 0.587 * g + 0.114 * b; // luminancia
        if (lum < 128) byte |= (1 << (7 - bit));       // píxel oscuro = 1
      }
      body.push(byte);
    }
  }
 
  // Avance de papel al final
  const footer = [0x1B, 0x64, 0x04]; // ESC d n — avanzar 4 líneas
 
  return new Uint8Array([...header, ...body, ...footer]);
};


// --- Componente auxiliar para encabezados de sección ---
const SectionHeader = ({ label }) => (
  <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
    <Divider>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'primary.main',
          px: 1,
        }}
      >
        {label}
      </Typography>
    </Divider>
  </Box>
);

// --- Definición del Estado Inicial del Formulario ---
const initialFormData = {
  id_paciente: '',
  createdAt: '',
  updatedAt: '',
  nombre_paciente: '',
  id_raza: '',
  fecha_nac_paciente: null,
  es_fallecido: false,
  fecha_fallecido: null, // Nuevo
  razon_deceso: '',      // Nuevo
  alimentacion: '',      // Nuevo
  es_castrado: false,
  es_exportado: false,
  es_cambio_propietario: false,
  es_extraviado: false,
  edad: '',
  caracter: '',
  pelo: '',
  ojos: '',
  habitad: '',
  tamanio: '',
  tatuaje: '',
  microchip: '',
  expediente_fisico: '',
  pedigri: '',
  observaciones: '',
  sexo_paciente: null,
  qr_code: '',
};

const PacienteForm = ({ visible, onClose, pacienteId, onSave, id_sucursal, id_cliente, sucursalNombre = "", razas = [], especies = [] }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [alergias, setAlergias] = useState([]);
  const [alergiasCatalog, setAlergiasCatalog] = useState([]);

  // Cálculo de la edad
  const edadCalculada = useMemo(() => {
    const isoDateString = formData.fecha_nac_paciente && formData.fecha_nac_paciente.format
      ? formData.fecha_nac_paciente.format('YYYY-MM-DD')
      : formData.fecha_nac_paciente;
    return calcularEdadDetallada(isoDateString);
  }, [formData.fecha_nac_paciente]);

  const showSnackbar = useCallback((message, isError = false) => {
    setSnackbarMessage(message);
    setError(isError ? message : null);
    setSnackbarOpen(true);
  }, []);

  // Cargar datos del paciente y catálogos
  useEffect(() => {
    if (!visible) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const alergiasRes = await api.get('/alergias/');
        setAlergiasCatalog(alergiasRes.data || []);

        if (pacienteId) {
          const pacienteRes = await api.get(`/pacientes/${pacienteId}`);
          const pacienteData = pacienteRes.data;

          const formattedData = {
            id_paciente: pacienteData.id_paciente || '',
            qr_code: pacienteData.qr_code || '',
            createdAt: pacienteData.createdAt || '', // Guarda el número/timestamp directamente
            updatedAt: pacienteData.updatedAt || '', // No lo conviertas a string aquí
            nombre_paciente: pacienteData.nombre_paciente || '',
            id_raza: pacienteData.id_raza || '',
            fecha_nac_paciente: pacienteData.fecha_nac_paciente ? dayjs(pacienteData.fecha_nac_paciente) : null,
            edad: pacienteData.edad || '',
            caracter: pacienteData.caracter || '',
            pelo: pacienteData.pelo || '',
            ojos: pacienteData.ojos || '',
            habitad: pacienteData.habitad || '',
            tamanio: pacienteData.tamanio || '',
            tatuaje: pacienteData.tatuaje || '',
            microchip: pacienteData.microchip || '',
            expediente_fisico: pacienteData.expediente_fisico || '',
            pedigri: pacienteData.pedigri || '',
            observaciones: pacienteData.observaciones || '',
            es_fallecido: pacienteData.es_fallecido || false,
            fecha_fallecido: pacienteData.fecha_fallecido ? dayjs(pacienteData.fecha_fallecido) : null,
            razon_deceso: pacienteData.razon_deceso || '',
            alimentacion: pacienteData.alimentacion || '',
            es_exportado: pacienteData.es_exportado || false,
            es_castrado: pacienteData.es_castrado || false,
            es_cambio_propietario: pacienteData.es_cambio_propietario || false,
            es_extraviado: pacienteData.es_extraviado || false,
            sexo_paciente: (pacienteData.sexo_paciente === 0 || pacienteData.sexo_paciente === 1) ? pacienteData.sexo_paciente : null,
          };
          setFormData(formattedData);

          const alergiasPacienteRes = await api.get('/alergias-pacientes/');
          const alergiasFiltered = alergiasPacienteRes.data.filter(a => a.id_paciente === pacienteId);
          setAlergias(alergiasFiltered);

        } else {
          setFormData(initialFormData);
          setAlergias([]);
        }
      } catch (err) {
        const msg = 'Error al cargar datos: ' + (err.response?.data?.detail || err.message);
        showSnackbar(msg, true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pacienteId, visible, showSnackbar]);

// Listener para cerrar con F2 o Escape si el formulario está visible
  useEffect(() => {
    if (!visible) return;

    const handleKeyDownForm = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDownForm);
    return () => window.removeEventListener('keydown', handleKeyDownForm);
  }, [visible, onClose]);



  // === FUNCIÓN PARA VER PDF ===
const handleAbrirQR = async () => {
  if (!formData.qr_code) {
    showSnackbar('No hay código QR disponible', true);
    return;
  }

  try {
    const response = await api.get(`/pacientes/qr/pdf/${formData.qr_code}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);

    // ← Esta es la línea clave: abre en nueva pestaña en vez de descargar
    window.open(blobUrl, '_blank');

    // Limpieza recomendada (evita consumir mucha memoria)
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 60000); // 60 segundos

    showSnackbar('PDF abierto en nueva pestaña');

  } catch (err) {
    console.error(err);
    showSnackbar('Error al abrir el PDF: ' + (err.response?.data?.detail || err.message), true);
  }
};

  const handleImprimirP1Local = async () => {
  if (loading) return;
  if (!formData.qr_code) {
    showSnackbar('Este paciente no tiene código QR.', true);
    return;
  }

  let port;
  try {
    setLoading(true);

    const DENSITY = 15;
    const SPEED = 1;
    const BW_THRESHOLD = 200; 
    const QR_SIZE_PX = 285;     // ← Reducido para dejar espacio al texto

    port = await navigator.serial.requestPort();
    await port.open({ baudRate: 9600 });

    const qrUrl = `${api.defaults.baseURL}/pacientes/qr/imagen/${formData.qr_code}`;
    const imgResponse = await fetch(qrUrl, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    
    const blob = await imgResponse.blob();
    const bitmap = await createImageBitmap(blob);

    const canvas = document.createElement('canvas');
    canvas.width = QR_SIZE_PX;
    canvas.height = QR_SIZE_PX;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, QR_SIZE_PX, QR_SIZE_PX);
    ctx.drawImage(bitmap, 0, 0, QR_SIZE_PX, QR_SIZE_PX);
    
    const imageData = ctx.getImageData(0, 0, QR_SIZE_PX, QR_SIZE_PX);
    const pixels = imageData.data;
    const bytesPerRow = Math.ceil(QR_SIZE_PX / 8); 
    const bitmapBuffer = new Uint8Array(bytesPerRow * QR_SIZE_PX);

    // === LÓGICA INVERSA ORIGINAL (la que te funcionaba) ===
    for (let y = 0; y < QR_SIZE_PX; y++) {
      for (let xb = 0; xb < bytesPerRow; xb++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = xb * 8 + bit;
          if (x >= QR_SIZE_PX) break;
          
          const idx = (y * QR_SIZE_PX + x) * 4;
          const r = pixels[idx], g = pixels[idx + 1], b = pixels[idx + 2];
          const lum = 0.299 * r + 0.587 * g + 0.114 * b;

          if (lum >= BW_THRESHOLD) {           // ← Lógica inversa que te funcionaba
            byte |= (0x80 >> bit);
          }
        }
        bitmapBuffer[y * bytesPerRow + xb] = byte;
      }
    }

    const encoder = new TextEncoder();
    const now = dayjs();

    const header = encoder.encode(
      `SIZE 48 mm,48 mm\r\n` +
      `GAP 2 mm,0\r\n` +
      `DIRECTION 0\r\n` +
      `DENSITY ${DENSITY}\r\n` +
      `SPEED ${SPEED}\r\n` +
      `CLS\r\n` +
      `BITMAP 24,5,${bytesPerRow},${QR_SIZE_PX},0,`
    );

    const footer = encoder.encode(
      `\r\n` +
      `TEXT 24,${QR_SIZE_PX + 8},"3",0,1,1,"${formData.nombre_paciente}"\r\n` +
      `TEXT 24,${QR_SIZE_PX + 36},"2",0,1,1,"${now.format('DD/MM/YYYY HH:mm')}"\r\n` +
      `TEXT 24,${QR_SIZE_PX + 64},"2",0,1,1,"${formData.qr_code}"\r\n` +
      `TEXT 24,${QR_SIZE_PX + 82},"1",0,1,1,"${sucursalNombre}"\r\n` +
      `PRINT 1,1\r\n`
    );

    const writer = port.writable.getWriter();
    await writer.write(header);
    await writer.write(bitmapBuffer);
    await writer.write(footer);
    
    writer.releaseLock();
    await port.close();

    showSnackbar("✓ Impresión con nombre, fecha y código");

  } catch (err) {
    console.error(err);
    showSnackbar("Error: " + err.message, true);
  } finally {
    setLoading(false);
  }
};
 
// NOTA: La función prepareP1Buffer ya NO se necesita.
// Puedes eliminarla de tu archivo PacienteForm.js.

  const handleGenerarQR = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.post(`/pacientes/${pacienteId}/generar-qr`);
      setFormData(prev => ({ ...prev, qr_code: res.data.qr_code }));
      showSnackbar('QR generado correctamente');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al generar QR';
      showSnackbar(msg, true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  const handleSave = async () => {
    if (!formData.nombre_paciente.trim()) {
      showSnackbar('El nombre del paciente es obligatorio', true);
      return;
    }
    if (!formData.id_raza) {
      showSnackbar('La raza es obligatoria', true);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        id_raza: parseInt(formData.id_raza),
        fecha_nac_paciente: formData.fecha_nac_paciente ? formData.fecha_nac_paciente.format('YYYY-MM-DD') : null,
        fecha_fallecido: formData.es_fallecido && formData.fecha_fallecido ? formData.fecha_fallecido.format('YYYY-MM-DD') : null,
        edad: edadCalculada,
        sexo_paciente: (formData.sexo_paciente === 0 || formData.sexo_paciente === 1) ? formData.sexo_paciente : null,
        id_cliente: parseInt(id_cliente),
        id_sucursal: parseInt(id_sucursal),
        qr_code: formData.qr_code.trim() === '' ? null : formData.qr_code,
        id_paciente: undefined,
        createdAt: undefined,
        updatedAt: undefined,
      };

      if (pacienteId) {
        await api.put(`/pacientes/${pacienteId}`, payload);
        showSnackbar('Paciente actualizado exitosamente');
        setFormData(prevData => ({
          ...prevData,
          updatedAt: Date.now()
        }));
      } else {
        await api.post('/pacientes/', payload);
        showSnackbar('Paciente creado exitosamente');
      }

      setTimeout(() => { onSave(); }, 1000);

    } catch (err) {
      const msg = 'Error al guardar paciente: ' + (err.response?.data?.detail || err.message);
      showSnackbar(msg, true);
    } finally {
      setLoading(false);
    }
  };

  const selectedRaza = useMemo(() =>
    razas.find(r => r.id_raza === formData.id_raza) || null,
    [formData.id_raza, razas]
  );

const filteredRazas = useMemo(() => {
    // Filtrar para incluir únicamente las razas habilitadas (habilitada === true)
    const opciones = razas.filter(r => r.habilitada === true);
    
    if (pacienteId && selectedRaza && selectedRaza.habilitada === false) {
      if (!opciones.some(r => r.id_raza === selectedRaza.id_raza)) {
        opciones.push(selectedRaza);
      }
    }
    return opciones;
  }, [razas, pacienteId, selectedRaza]);

  if (!visible) return null;

  

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: 2.5, bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>

        {/* ── Encabezado ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {pacienteId ? 'Editar Paciente' : 'Nuevo Paciente'}
          </Typography>
          {loading && <CircularProgress size={20} />}
        </Box>

        {/* ── Metadatos como chips de solo lectura ── */}
{pacienteId && (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, gap: 0.75 }}>
    <Chip
      size="small"
      label={`ID: ${formData.id_paciente}`}
      variant="outlined"
      sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
    />
    {formData.createdAt && (
      <Chip
        size="small"
        label={`Creado: ${dayjs(Number(formData.createdAt)).format('DD/MM/YYYY hh:mm A')}`}
        variant="outlined"
        sx={{ fontSize: '0.72rem' }}
      />
    )}
    {formData.updatedAt && (
      <Chip
        size="small"
        label={`Actualizado: ${dayjs(Number(formData.updatedAt)).format('DD/MM/YYYY hh:mm A')}`}
        variant="outlined"
        color="primary"
        sx={{ fontSize: '0.72rem' }}
      />
    )}
  </Stack>
)}

        {/* ── Contenido scrollable ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>

          {/* ════ Sección: Identificación ════ */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 0.5 }}>
            <SectionHeader label="Identificación" />
            <TextField
              label="Nombre *"
              fullWidth
              name="nombre_paciente"
              value={formData.nombre_paciente}
              onChange={handleChange}
              size="small"
              sx={{ gridColumn: '1 / -1' }}
            />
                          <Autocomplete
                options={filteredRazas}
                // Mantenemos tu etiqueta
                getOptionLabel={(option) => {
                  if (!option || !option.id_raza) return '';
                  const especie = especies.find(e => e.id_especie === option.id_especie);
                  return `${option.nombre_raza || 'N/A'} - ${especie ? especie.nombre_especie : 'N/A'}`;
                }}
                // SOLUCIÓN 1: Forzar key única por ID
                renderOption={(props, option) => (
                  <li {...props} key={option.id_raza}>
                    {option.nombre_raza} - {especies.find(e => e.id_especie === option.id_especie)?.nombre_especie || 'N/A'}
                  </li>
                )}
                // SOLUCIÓN 2: Comparación segura por ID
                isOptionEqualToValue={(option, value) => option.id_raza === value.id_raza}
                value={selectedRaza}
                onChange={(e, newValue) => setFormData({ ...formData, id_raza: newValue ? newValue.id_raza : '' })}
                renderInput={(params) => <TextField {...params} label="Raza *" fullWidth size="small" />}
                sx={{ gridColumn: '1 / -1' }}
              />
            <TextField
              select
              label="Sexo"
              fullWidth
              name="sexo_paciente"
              value={formData.sexo_paciente !== null ? formData.sexo_paciente : ''}
              onChange={handleChange}
              size="small"
            >
              <MenuItem value="">No especificado</MenuItem>
              <MenuItem value={1}>Macho</MenuItem>
              <MenuItem value={0}>Hembra</MenuItem>
            </TextField>
            <TextField
              label="Tamaño"
              fullWidth
              name="tamanio"
              value={formData.tamanio}
              onChange={handleChange}
              size="small"
            />
            <DatePicker
                label="Fecha de Nacimiento"
                value={formData.fecha_nac_paciente}
                format="DD/MM/YYYY"
                onChange={(newValue) => setFormData(prev => ({ ...prev, fecha_nac_paciente: newValue }))}
                
                // 1. Forzar a que abra directamente en la vista de selección de año
                openTo="year" 
                
                // 2. Definir el flujo de pantallas: Primero Año, luego Mes, finalmente Día
                views={['year', 'month', 'day']} 
                
                // 3. Modificaciones de UI y corrección de bug de posicionamiento (Popper)
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    size: 'small' 
                  },
                  popper: {
                    strategy: 'fixed', // Evita que salte al punto (0,0) de la pantalla al cambiar de año
                  }
                }}
              />
            <TextField
              label="Edad"
              fullWidth
              value={edadCalculada}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ '& .MuiInputBase-input': { color: 'text.secondary', fontStyle: 'italic' } }}
            />
          </Box>

          {/* ════ Sección: Características ════ */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5, mb: 0.5 }}>
            <SectionHeader label="Características físicas" />
            <TextField label="Carácter" size="small" fullWidth name="caracter" value={formData.caracter} onChange={handleChange} />
            <TextField label="Pelo" size="small" fullWidth name="pelo" value={formData.pelo} onChange={handleChange} />
            <TextField label="Ojos" size="small" fullWidth name="ojos" value={formData.ojos} onChange={handleChange} />
            <TextField label="Hábitat" size="small" fullWidth name="habitad" value={formData.habitad} onChange={handleChange} />
          </Box>

          {/* ════ Sección: Documentos ════ */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5, mb: 0.5 }}>
            <SectionHeader label="Documentos e identificadores" />
            <TextField label="Microchip" size="small" fullWidth name="microchip" value={formData.microchip} onChange={handleChange} />
            <TextField label="Tatuaje" size="small" fullWidth name="tatuaje" value={formData.tatuaje} onChange={handleChange} />
            <TextField label="Expediente" size="small" fullWidth name="expediente_fisico" value={formData.expediente_fisico} onChange={handleChange} />
            <TextField label="Pedigrí" size="small" fullWidth name="pedigri" value={formData.pedigri} onChange={handleChange} />
          </Box>

          {/* ════ Sección: Estado del paciente ════ */}
          <Box sx={{ mt: 1.5 }}>
            <SectionHeader label="Estado Actual" />
            <Stack direction="row" flexWrap="wrap" sx={{ mt: 1, gap: 0 }}>
              {[
                { name: 'es_fallecido', label: 'Fallecido' },
                { name: 'es_castrado', label: 'Esterilizado/Castrado' },
                { name: 'es_exportado', label: 'Exportado' },
                { name: 'es_cambio_propietario', label: 'Cambio de propietario' },
                { name: 'es_extraviado', label: 'Extraviado' },
              ].map(({ name, label }) => (
                <FormControlLabel
                  key={name}
                  control={
                    <Checkbox
                      checked={formData[name] || false}
                      onChange={handleChange}
                      name={name}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{label}</Typography>}
                  sx={{ mr: 1.5 }}
                />
              ))}
            </Stack>

            {/* Campos condicionales para Deceso */}
            {formData.es_fallecido && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <DatePicker
                  label="Fecha de Deceso"
                  value={formData.fecha_fallecido ? dayjs(formData.fecha_fallecido) : null}
                  format="DD/MM/YYYY"
                  onChange={(val) => setFormData(prev => ({ ...prev, fecha_fallecido: val }))}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
                <TextField
                  label="Razón del deceso"
                  size="small"
                  fullWidth
                  name="razon_deceso"
                  value={formData.razon_deceso || ''}
                  onChange={handleChange}
                />
              </Box>
            )}
          </Box>

          {/* ════ Cuidados y Notas ════ */}
          <Box sx={{ mt: 1.5, display: 'grid', gap: 1.5 }}>
            <SectionHeader label="Cuidados y Notas" />
            <TextField
              label="Alimentación"
              fullWidth
              name="alimentacion"
              value={formData.alimentacion || ''}
              onChange={handleChange}
              multiline
              rows={2}
              size="small"
              placeholder="Detalles sobre dieta y nutrición..."
              sx={{
                '& .MuiInputBase-inputMultiline': {
                  resize: 'vertical', // Permite estirar manualmente verticalmente
                },
              }}
            />
            <TextField
              label="Observaciones"
              fullWidth
              name="observaciones"
              value={formData.observaciones || ''}
              onChange={handleChange}
              multiline
              rows={2}
              size="small"
              placeholder="Notas médicas o de comportamiento..."
              sx={{
                '& .MuiInputBase-inputMultiline': {
                  resize: 'vertical', // Permite estirar manualmente verticalmente
                },
              }}
            />
          </Box>

          {/* ════ Sección: Código QR ════ */}
          {pacienteId && (
            <Box sx={{ mt: 1.5 }}>
              <SectionHeader label="Código QR" />
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                {formData.qr_code ? (
                  <>
                    <Box
                      component="img"
                      src={`${api.defaults.baseURL}/pacientes/qr/imagen/${formData.qr_code}`}
                      alt="QR"
                      sx={{ width: 80, height: 80, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    />
                    <Box>
                      <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', color: 'text.secondary', mb: 1 }}>
                        {formData.qr_code}
                      </Typography>
                      <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAbrirQR}     // ← Cambiado
                          disabled={!formData.qr_code || loading}
                        >
                          Ver / Abrir QR (PDF)
                        </Button>
                    </Box>
                  </>
                ) : (
                  <Button variant="outlined" size="small" onClick={handleGenerarQR} disabled={loading}>
                    Generar Código QR
                  </Button>
                )}
                {/* NUEVO BOTÓN PARA LA P1 */}
                
  <Button
    size="small"
    variant="contained"
    color="primary"
    onClick={handleImprimirP1Local}
    disabled={loading}
    startIcon={loading && <CircularProgress size={14} />}
  >
    Imprimir P1
  </Button>
              </Box>
            </Box>
          )}

          {/* ════ Sección: Alergias ════ */}
          {pacienteId && (
            <Box sx={{ mt: 1.5 }}>
              <SectionHeader label="Alergias" />
              <PacienteAlergias
                pacienteId={pacienteId}
                alergias={alergias}
                setAlergias={setAlergias}
                alergiasCatalog={alergiasCatalog}
                showSnackbar={showSnackbar}
                disabled={loading}
              />
            </Box>
          )}
        </Box>
        {/* fin scroll */}

        {/* ── Botones de acción (fijos al fondo del Drawer) ── */}
        <Box
          sx={{
            pt: 1.5,
            mt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button onClick={onClose} color="secondary" size="small">
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" size="small" disabled={loading}>
            {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Guardar
          </Button>
        </Box>

        {/* ── Snackbar ── */}
        <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
          <Alert severity={error ? 'error' : 'success'} onClose={() => setSnackbarOpen(false)}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

      </Box>
    </LocalizationProvider>
  );
};

export default PacienteForm;